import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";

interface UseSessionStateOptions<T> {
  /**
   * Storage key (will be automatically prefixed with route path)
   */
  key: string;

  /**
   * Default value if nothing is stored
   */
  defaultValue: T;

  /**
   * Custom serializer (optional)
   * Default: JSON.stringify
   */
  serialize?: (value: T) => string;

  /**
   * Custom deserializer (optional)
   * Default: JSON.parse
   */
  deserialize?: (value: string) => T;
}

/**
 * Custom hook that manages UI state in sessionStorage with route-based namespacing
 *
 * @example
 * const [state, setState] = useSessionState({
 *   key: 'ui-state',
 *   defaultValue: { dialogOpen: false },
 * });
 */
export function useSessionState<T>({
  key,
  defaultValue,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
}: UseSessionStateOptions<T>): [T, (value: T | ((prev: T) => T)) => void] {
  const location = useLocation();

  // Create a scoped key using the current route path
  const scopedKey = `${location.pathname}:${key}`;

  // Lazy initialization - only read from storage on mount
  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(scopedKey);
      if (stored === null) {
        return defaultValue;
      }
      return deserialize(stored);
    } catch (error) {
      console.warn(
        `Failed to parse session state for key "${scopedKey}":`,
        error,
      );
      return defaultValue;
    }
  });

  // Update sessionStorage whenever state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(scopedKey, serialize(state));
    } catch (error) {
      console.error(
        `Failed to save session state for key "${scopedKey}":`,
        error,
      );
    }
  }, [state, scopedKey, serialize]);

  // Wrapper to support functional updates like useState
  const updateState = useCallback((value: T | ((prev: T) => T)) => {
    setState(value);
  }, []);

  return [state, updateState];
}
